import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type {
  CreateSessionRequest,
  CreateSessionResponse,
  RecordResponseRequest,
  RecordResponseResponse,
  CompleteSessionResponse,
} from '@/types/session';

/**
 * Hook to create a new quiz session
 *
 * Automatically creates a session when a quiz starts.
 * Error handling is silent - quiz continues even if session creation fails.
 */
export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation<CreateSessionResponse, Error, CreateSessionRequest>({
    mutationFn: (data: CreateSessionRequest) => api.createSession(data),
    onSuccess: (response) => {
      // Cache the session ID for later use
      queryClient.setQueryData(
        queryKeys.sessions.detail(response.sessionId),
        { id: response.sessionId }
      );
    },
    onError: (error) => {
      // Silent failure - don't break quiz experience
      console.warn('Failed to create quiz session:', error);
    },
  });
}

/**
 * Hook to record a question response in real-time
 *
 * Records each answer as it's submitted with grading results.
 * Fire-and-forget - doesn't block quiz progression.
 */
export function useRecordResponse() {
  return useMutation<
    RecordResponseResponse,
    Error,
    { sessionId: string; data: RecordResponseRequest }
  >({
    mutationFn: ({ sessionId, data }) => api.recordResponse(sessionId, data),
    onError: (error) => {
      // Silent failure - don't break quiz experience
      console.warn('Failed to record question response:', error);
    },
    // Don't retry failed requests to avoid blocking the quiz
    retry: false,
  });
}

/**
 * Hook to complete a quiz session
 *
 * Finalizes the session and calculates aggregate metrics.
 * Non-critical - data is already recorded per-question.
 */
export function useCompleteSession() {
  const queryClient = useQueryClient();

  return useMutation<CompleteSessionResponse, Error, string>({
    mutationFn: (sessionId: string) => api.completeSession(sessionId),
    onSuccess: (response, sessionId) => {
      // Update cached session data
      queryClient.setQueryData(
        queryKeys.sessions.detail(sessionId),
        response.session
      );

      // Invalidate sessions list if needed
      queryClient.invalidateQueries({
        queryKey: queryKeys.sessions.all,
      });
    },
    onError: (error) => {
      // Silent failure - session metrics are already recorded
      console.warn('Failed to complete quiz session:', error);
    },
  });
}
